import produce from 'immer';
import { uniqBy } from 'lodash';
import { handleActions } from 'redux-actions';
import { setUncheckableTableTransferList, updateStoreWithPath } from '../../../../lib/common';
import { RECOMMEND_PRODUCT_TYPE } from '../../../../constants/service/app/recommendProductConstants';
import { _LIST_PAGINATION_MODEL } from '../../app/models';
import actions from './actions';
import {
  RECOMMEND_PRODUCT_LIST_SEARCH_DATA_MODEL,
  RECOMMEND_PRODUCT_LIST_SEARCH_OPTION_MODEL,
  RECOMMEND_PRODUCT_SAVE_MODEL,
} from './models';

/**
 *  INITIAL STATE
 */
const initialState = {
  /**
   *  추천 상품 리스트
   */
  recommendProductList: {
    /**
     *  리스트
     */
    list: [],
    /**
     *  검색 박스
     */
    searchData: RECOMMEND_PRODUCT_LIST_SEARCH_DATA_MODEL,
    /**
     *  검색 옵션
     */
    searchOption: RECOMMEND_PRODUCT_LIST_SEARCH_OPTION_MODEL,
    /**
     *  리스트 pagination
     */
    pagination: _LIST_PAGINATION_MODEL,
  },

  /**
   *  추천 상품 상세
   */
  recommendProductDetail: {
    /**
     *  추천 상품 정보
     */
    recommendProductInfo: {
      /**
       *  추천 상품 식별 id
       */
      recommendProductId: null,
      /**
       *  추천인
       */
      adminName: null,
      /**
       *  추천 상품 타입
       */
      type: RECOMMEND_PRODUCT_TYPE.POPULAR_POP,
      /**
       *  추천 상품 생성일
       */
      createDate: null,
      /**
       *  메모
       */
      memo: null,
    },
    /**
     *  추천 상품 아이템 리스트
     */
    recommendProductItemList: [],
  },

  /**
   *  추천 상품 등록/복사
   */
  recommendProductSave: RECOMMEND_PRODUCT_SAVE_MODEL,

  /**
   *  상품 1차 카테고리 리스트
   */
  mainProductCategorySelectList: [],
  /**
   *  상품 2차 카테고리 리스트
   */
  subProductCategorySelectList: {
    [null]: [],
  },
};

/**
 *  REDUCER
 */
export default handleActions(
  {
    /**
     *  추천 상품 데이터 변경하기 성공
     */
    [actions.SET_DATA]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath(action.payload.path, action.payload.value, draft);
      });
    },

    /**
     *  추천 상품 리스트 가져오기 성공
     */
    [actions.GET_RECOMMEND_PRODUCT_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.recommendProductList = {
          ...draft.recommendProductList,
          ...action.payload.recommendProductList,
        };
      });
    },

    /**
     *  추천 상품 상세 요청하기 성공
     */
    [actions.GET_RECOMMEND_PRODUCT_DETAIL_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.recommendProductDetail = action.payload.recommendProductDetail;
      });
    },

    /**
     *  추천 상품 복사하기 전 필요한 데이터 가져오기 성공
     */
    [actions.PREPARE_COPY_RECOMMEND_PRODUCT_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택할 상품 리스트
         */
        draft.recommendProductSave.recommendProductItemList.itemList = {
          ...draft.recommendProductSave.recommendProductItemList.itemList,
          ...action.payload.recommendProductSave.recommendProductItemList,
        };

        /**
         *  상품 정보
         */
        draft.recommendProductSave.recommendProductInfo = action.payload.recommendProductSave.recommendProductInfo;

        /**
         *  선택된 상품 리스트
         */
        draft.recommendProductSave.recommendProductItemList.selectedItemList = {
          list: action.payload.recommendProductSave.selectedRecommendProductItemList,
          idList: action.payload.recommendProductSave.selectedRecommendProductItemList.map((item) => item.id),
        };

        /**
         *  선택할 상품 아이템 중에 이미 설정된 상품 아이템인 경우에는
         *  상품 아이템 선택 부분에서 이동을 할 수 없도록 하기
         */
        draft.recommendProductSave.recommendProductItemList.itemList.list = setUncheckableTableTransferList({
          list: draft.recommendProductSave.recommendProductItemList.itemList.list,
          selectedIdList: draft.recommendProductSave.recommendProductItemList.selectedItemList.idList,
        });

        /**
         *  상품 카테고리 SELECT LIST
         */
        draft.mainProductCategorySelectList = action.payload.mainProductCategorySelectList;
        draft.subProductCategorySelectList = action.payload.subProductCategorySelectList;
      });
    },

    /**
     *  추천 상품 등록하기 전 필요한 데이터 가져오기 성공
     */
    [actions.PREPARE_CREATE_RECOMMEND_PRODUCT_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  상품 리스트
         */
        draft.recommendProductSave.recommendProductItemList.itemList =
          action.payload.recommendProductSave.recommendProductItemList;

        /**
         *  상품 카테고리 SELECT LIST
         */
        draft.mainProductCategorySelectList = action.payload.mainProductCategorySelectList;
        draft.subProductCategorySelectList = action.payload.subProductCategorySelectList;
      });
    },

    /**
     *  추천 상품에 연결할 아이템 리스트 요청하기 성공
     */
    [actions.GET_RECOMMEND_PRODUCT_ITEM_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택할 상품 리스트
         */
        draft.recommendProductSave.recommendProductItemList.itemList = {
          ...draft.recommendProductSave.recommendProductItemList.itemList,
          ...action.payload.recommendProductItemList,
        };

        /**
         *  선택할 상품 아이템 중에 이미 설정된 상품 아이템인 경우에는
         *  상품 아이템 선택 부분에서 이동을 할 수 없도록 하기
         */
        draft.recommendProductSave.recommendProductItemList.itemList.list = setUncheckableTableTransferList({
          list: draft.recommendProductSave.recommendProductItemList.itemList.list,
          selectedIdList: draft.recommendProductSave.recommendProductItemList.selectedItemList.idList,
        });
      });
    },

    /**
     *  상품 리스트 EXCEL 업로드 성공
     */
    [actions.SET_RECOMMEND_PRODUCT_ITEM_LIST_EXCEL_UPLOAD_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택된 상품 리스트
         */
        draft.recommendProductSave.recommendProductItemList.selectedItemList.list = action.payload.itemList;

        /**
         *  선택된 상품 아이템의 중복 제거하기
         */
        draft.recommendProductSave.recommendProductItemList.selectedItemList.list = uniqBy(
          draft.recommendProductSave.recommendProductItemList.selectedItemList.list,
          'id',
        );

        draft.recommendProductSave.recommendProductItemList.selectedItemList.idList =
          draft.recommendProductSave.recommendProductItemList.selectedItemList.list.map((item) => item.id);

        /**
         *  선택할 상품 아이템 중에 이미 설정된 상품 아이템인 경우에는
         *  상품 아이템 선택 부분에서 이동을 할 수 없도록 하기
         */
        draft.recommendProductSave.recommendProductItemList.itemList.list = setUncheckableTableTransferList({
          list: draft.recommendProductSave.recommendProductItemList.itemList.list,
          selectedIdList: draft.recommendProductSave.recommendProductItemList.selectedItemList.idList,
        });
      });
    },
  },
  initialState,
);
